/**
 * Hook for creating and fetching search snapshots.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '../services/authApi';
import type { SnapshotResponse } from '../types';

/**
 * Hook for fetching a snapshot by share token.
 */
export function useSnapshotQuery(shareToken: string | undefined) {
  return useQuery<SnapshotResponse>({
    queryKey: ['snapshot', shareToken],
    queryFn: () => authApi.getSnapshot(shareToken!),
    enabled: !!shareToken,
    staleTime: Infinity, // Snapshots are immutable
  });
}

/**
 * Hook for creating a snapshot (mutation-like with useState).
 */
export function useCreateSnapshot() {
  const [isCreating, setIsCreating] = useState(false);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const createSnapshot = async (data: {
    query: string;
    drug_results: unknown[];
    filters?: Record<string, unknown>;
    intent?: unknown;
    evidence_chunks?: unknown[];
    summary?: string | null;
    metadata?: unknown;
    result_count?: number;
  }) => {
    setIsCreating(true);
    setError(null);
    try {
      const result = await authApi.createSnapshot(data);
      setShareToken(result.share_token);
      return result;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create snapshot';
      setError(message);
      throw err;
    } finally {
      setIsCreating(false);
    }
  };

  return { createSnapshot, isCreating, shareToken, error };
}
